import { DateTime } from '@ksyos/date-time';
import { Button, Card, FormButtonRow, FormGroup, PageHeader } from '@ksyos/design-system';
import { DebtorType, ManagerAuthorization } from '@common/enums';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { assertHasAuthorization } from '@frontend/utils';
import { FrontendSettingsContext } from '@frontend/utils/hooks/useFrontendSettings';
import type { IInvoiceBatchInformation } from '../../../../common/utils/invoiceBatchInformation';
import type {
    GroupChoice,
    OrganizationChoice,
    ServiceChoice,
} from '../../../../common/validation/schemas/choiceSchema';
import type { ValidationErrors } from '../../../utils/validation';
import type { IGroup } from '../../interfaces/group';
import type { IOption } from '../../interfaces/option';
import type { IService } from '../../interfaces/service';
import { BatchType } from '../../../../common/enums/batchType';
import { InvoiceRecipient } from '../../../../common/enums/invoiceRecipient';
import { invoiceRecordsExportSchema } from '../../../../common/validation/invoiceRecordsExport';
import { Choice, FilterChoice } from '../../../../common/validation/schemas/choiceSchema';
import { ExactAuthentication } from '../../../components/elements/ExactAuthentication';
import { ExactClientEventProgress } from '../../../components/elements/ExactClientEventProgress';
import {
    DownloadProgressFeedbackType,
    ExportDownloadProgress,
} from '../../../components/elements/ExportDownloadProgress';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { BatchesTable } from '../../elements/BatchesTable';
import {
    DateInput,
    Dropdown,
    DropdownSearch,
    GroupedDropdown,
    InputCheckbox,
    InputRadioGroup,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { ConfirmationModal } from '../../elements/modals/ConfirmationModal';

interface IServiceOption {
    group: string;
    text: string;
    value: string;
}

interface IOrganizationOptions {
    city: string | null;
    name: string;
}

interface IFilterValues {
    text: string;
    value: string;
}

interface ISearchParams {
    batchId: string | null;
    closedBeforeDate: string | null;
    episodeCategoryFilterValues: IFilterValues[];
    episodeCategoryId: string | null;
    filterChoice: FilterChoice | null;
    groupChoice: GroupChoice;
    groupId: string | null;
    isPreview: boolean;
    organizationChoice: OrganizationChoice;
    organizationName: string | null;
    serviceChoice: ServiceChoice;
    serviceId: string | null;
}

interface IInvoiceRecordsExportState {
    count: number;
    errors: ValidationErrors;
    exactAllowsNormalExport: boolean;
    exactClientEventId?: string;
    exportDownloadFinishedWithErrors?: boolean;
    exportDownloadId?: string;
    groupName?: string;
    organizationName?: string;
    searchParams: ISearchParams;
    serviceOptions: IServiceOption[];
    showConfirmationModal: boolean;
}

export class InvoiceRecordsExport extends Component<{}, IInvoiceRecordsExportState> {
    constructor(props: {}) {
        super(props);
        this.state = {
            count: 0,
            errors: emptyValidationErrors,
            exactAllowsNormalExport: false,
            searchParams: {
                batchId: null,
                closedBeforeDate: null,
                episodeCategoryFilterValues: [],
                episodeCategoryId: null,
                filterChoice: null,
                groupChoice: Choice.all,
                groupId: null,
                isPreview: false,
                organizationChoice: Choice.all,
                organizationName: null,
                serviceChoice: Choice.all,
                serviceId: null,
            },
            serviceOptions: [],
            showConfirmationModal: false,
        };
    }

    public async componentDidMount() {
        assertHasAuthorization(ManagerAuthorization.exportInvoiceRecords);
    }

    public render() {
        return (
            <FrontendSettingsContext.Consumer>
                {(context) => (
                    <PageTemplate width="medium">
                        <PageHeader title={translateKey('finance.invoicing')} />
                        <Card>
                            <Card.Header title={translateKey('export.invoicesProcessExport')} />
                            <Card.Body>
                                <FormGroup
                                    hasErrors={this.state.errors.has('closedBeforeDate')}
                                    isRequired
                                    label={translateKey('common.closedAt')}
                                >
                                    <DateInput
                                        error={this.state.errors.get('closedBeforeDate')}
                                        hasMaxWidth={true}
                                        onChange={async (value) => await this.handleChange('closedBeforeDate', value)}
                                        onDoValidate={async () => await this.validateOne('closedBeforeDate')}
                                        placeholder={translateKey('common.dateFormat')}
                                        size="large"
                                        value={this.state.searchParams.closedBeforeDate}
                                        variant="outline"
                                    />
                                </FormGroup>

                                <FormGroup
                                    hasErrors={this.state.errors.has('filterChoice')}
                                    isRequired
                                    label={translateKey('common.filterOn')}
                                >
                                    <InputRadioGroup
                                        error={this.state.errors.get('filterChoice')}
                                        labels={[
                                            translateKey('groups.group'),
                                            translateKey('organization.organization'),
                                            translateKey('groups.group') +
                                                '/' +
                                                translateKey('organization.organization'),
                                            translateKey('export.compositePatientInvoice'),
                                            translateKey('export.singularPatientInvoice'),
                                        ]}
                                        name={'filterChoice'}
                                        onChange={async (value) => {
                                            await this.setState(({ searchParams }) => ({
                                                searchParams: {
                                                    ...searchParams,
                                                    filterChoice: value,
                                                    groupChoice: Choice.all,
                                                    groupId: this.isPatientInvoice(value) ? null : searchParams.groupId,
                                                    organizationChoice: Choice.all,
                                                    serviceChoice: this.isPatientInvoice(value)
                                                        ? Choice.all
                                                        : searchParams.serviceChoice,
                                                    serviceId: this.isPatientInvoice(value)
                                                        ? null
                                                        : searchParams.serviceId,
                                                },
                                            }));
                                            await this.loadEpisodeCategoryFilter(value);
                                        }}
                                        onDoValidate={async () => await this.validateOne('filterChoice')}
                                        options={[
                                            FilterChoice.group,
                                            FilterChoice.organization,
                                            FilterChoice.groupAndOrganization,
                                            FilterChoice.compositePatientInvoice,
                                            FilterChoice.singularPatientInvoice,
                                        ]}
                                        value={this.state.searchParams.filterChoice}
                                    />
                                </FormGroup>

                                {this.state.searchParams.filterChoice === FilterChoice.compositePatientInvoice &&
                                    this.showEpisodeCategory()}

                                {this.state.searchParams.filterChoice !== null &&
                                    !this.isPatientInvoice(this.state.searchParams.filterChoice) && (
                                        <>
                                            {this.showEpisodeCategory()}
                                            <FormGroup
                                                hasErrors={this.state.errors.has('serviceId')}
                                                label={translateKey('service.service')}
                                            >
                                                <InputRadioGroup
                                                    error={this.state.errors.get('serviceChoice')}
                                                    labels={[
                                                        translateKey(
                                                            'common.allItems',
                                                            translateKey('service.services')
                                                        ),
                                                        translateKey(
                                                            'common.specificItem',
                                                            translateKey('service.service')
                                                        ),
                                                    ]}
                                                    name={'serviceChoice'}
                                                    onChange={async (value) =>
                                                        await this.handleChange(
                                                            'serviceChoice',
                                                            value === Choice.all ? Choice.all : Choice.specific
                                                        )
                                                    }
                                                    options={['all', 'specific']}
                                                    radioButtonsStacked
                                                    value={this.state.searchParams.serviceChoice}
                                                />
                                                {this.state.searchParams.serviceChoice === 'specific' && (
                                                    <GroupedDropdown
                                                        error={this.state.errors.get('serviceId')}
                                                        notSelectedText={translateKey(
                                                            'common.selectItem',
                                                            translateKey('service.service')
                                                        )}
                                                        onChange={async (value) =>
                                                            await this.handleChange('serviceId', value)
                                                        }
                                                        onDoValidate={async () => await this.validateOne('serviceId')}
                                                        selectOptions={this.state.serviceOptions}
                                                        sort
                                                        value={this.state.searchParams.serviceId}
                                                    />
                                                )}
                                            </FormGroup>
                                        </>
                                    )}
                                {this.state.searchParams.filterChoice === FilterChoice.group && (
                                    <FormGroup hasErrors={this.state.errors.has('groups')}>
                                        <InputRadioGroup
                                            error={this.state.errors.get('groupChoice')}
                                            labels={[
                                                translateKey('common.allItems', translateKey('groups.groups')),
                                                translateKey('common.specificItem', translateKey('groups.group')),
                                            ]}
                                            name={'groupChoice'}
                                            onChange={async (value) =>
                                                await this.handleChange(
                                                    'groupChoice',
                                                    value === Choice.all ? Choice.all : Choice.specific
                                                )
                                            }
                                            onDoValidate={async () => await this.validateOne('groupChoice')}
                                            options={['all', 'specific']}
                                            radioButtonsStacked
                                            value={this.state.searchParams.groupChoice}
                                        />
                                        {this.state.searchParams.groupChoice === 'specific' && (
                                            <DropdownSearch
                                                error={this.state.errors.get('groupId')}
                                                loadOptions={async (input) => await this.loadGroups(input)}
                                                onChange={async (value, label) => {
                                                    await this.handleChange('groupId', value);
                                                    await this.setState({ groupName: label });
                                                }}
                                                onDoValidate={async () => await this.validateOne('groupId')}
                                                placeholder={translateKey('common.search') + '...'}
                                                value={this.state.searchParams.groupId}
                                            />
                                        )}
                                    </FormGroup>
                                )}

                                {this.state.searchParams.filterChoice === FilterChoice.organization && (
                                    <FormGroup hasErrors={this.state.errors.has('organizations')}>
                                        <InputRadioGroup
                                            error={this.state.errors.get('organizationChoice')}
                                            labels={[
                                                translateKey(
                                                    'common.allItems',
                                                    translateKey('organization.organizations')
                                                ),
                                                translateKey(
                                                    'common.specificItem',
                                                    translateKey('organization.organization')
                                                ),
                                            ]}
                                            name={'organizationChoice'}
                                            onChange={async (value) =>
                                                await this.handleChange(
                                                    'organizationChoice',
                                                    value === Choice.all ? Choice.all : Choice.specific
                                                )
                                            }
                                            onDoValidate={async () => await this.validateOne('organizationChoice')}
                                            options={['all', 'specific']}
                                            radioButtonsStacked
                                            value={this.state.searchParams.organizationChoice}
                                        />
                                        {this.state.searchParams.organizationChoice === 'specific' && (
                                            <DropdownSearch
                                                error={this.state.errors.get('organizationName')}
                                                loadOptions={async (input) => await this.loadOrganizations(input)}
                                                onChange={async (value, label) => {
                                                    await this.handleChange('organizationName', value);
                                                    await this.setState({ organizationName: label });
                                                }}
                                                onDoValidate={async () => await this.validateOne('organizationName')}
                                                placeholder={translateKey('common.search') + '...'}
                                                value={this.state.searchParams.organizationName}
                                            />
                                        )}
                                    </FormGroup>
                                )}

                                {context?.frontendSettings.exactIntegrationInvoicesEnabled &&
                                    this.state.searchParams.filterChoice !== null &&
                                    !this.isPatientInvoice(this.state.searchParams.filterChoice) && (
                                        <ExactAuthentication
                                            updateExactAllowsAction={async (value) =>
                                                await this.setState({ exactAllowsNormalExport: value })
                                            }
                                        />
                                    )}

                                <FormButtonRow>
                                    <InputCheckbox
                                        checked={this.state.searchParams.isPreview}
                                        name="isPreview"
                                        onChange={async (value) => await this.handleChange('isPreview', value)}
                                        text="Proefexport"
                                    />
                                    <Button
                                        isDisabled={
                                            !this.isPatientInvoice(this.state.searchParams.filterChoice) &&
                                            context?.frontendSettings.exactIntegrationInvoicesEnabled &&
                                            !this.state.exactAllowsNormalExport &&
                                            !this.state.searchParams.isPreview
                                        }
                                        onClick={async () => {
                                            await this.handleChange('batchId', null);
                                            await this.downloadExport();
                                        }}
                                    >
                                        {translateKey('export.export')}
                                    </Button>
                                </FormButtonRow>
                                <ExportDownloadProgress
                                    downloadProcessFeedbackType={DownloadProgressFeedbackType.progressPercentage}
                                    exportDownloadId={this.state.exportDownloadId}
                                    onFinished={async (error?: boolean) => {
                                        await this.setState({ exportDownloadFinishedWithErrors: !!error });
                                    }}
                                />

                                {context?.frontendSettings.exactIntegrationInvoicesEnabled &&
                                    this.state.exactClientEventId &&
                                    !this.state.searchParams.isPreview &&
                                    !this.state.exportDownloadFinishedWithErrors && (
                                        <ExactClientEventProgress exactClientEventId={this.state.exactClientEventId} />
                                    )}
                            </Card.Body>
                        </Card>
                        <Card>
                            <BatchesTable<IInvoiceBatchInformation>
                                allowRerun={this.state.exactAllowsNormalExport}
                                batchType={this.getBatchType()}
                                informationColumns={[
                                    {
                                        isDate: true,
                                        key: 'closedBeforeDate',
                                        label: translateKey('common.closedAt'),
                                    },
                                    {
                                        key: 'episodeCategory',
                                        label: translateKey('episodeCategory.episodeCategory'),
                                        translateKeyPrefix: 'episodeCategoryEnum',
                                    },
                                    { key: 'serviceName', label: translateKey('service.service') },
                                    { key: 'groupName', label: translateKey('groups.group') },
                                    {
                                        key: 'organizationName',
                                        label: translateKey('organization.organization'),
                                    },
                                ]}
                                onExport={async (batchId: string) => {
                                    await this.setState(({ searchParams }) => ({
                                        searchParams: {
                                            ...searchParams,
                                            batchId: batchId,
                                        },
                                    }));

                                    await this.downloadExport();
                                }}
                                showIterativeBatches={context?.frontendSettings.exactIntegrationInvoicesEnabled}
                            ></BatchesTable>
                        </Card>
                        <ConfirmationModal
                            handleNo={async () => await this.setState({ showConfirmationModal: false })}
                            handleYes={async () => {
                                await this.setState({ showConfirmationModal: false });

                                if (this.isPatientInvoice(this.state.searchParams.filterChoice)) {
                                    window.open(this.getUrl());
                                } else {
                                    const result = await fetchJson(this.getUrl(), 'GET');
                                    await this.setState({
                                        exactClientEventId: result.json.exactClientEventId,
                                        exportDownloadId: result.json.exportDownloadId,
                                    });
                                }
                            }}
                            isOpen={this.state.showConfirmationModal}
                            question={this.getConfirmationModalMessage()}
                            title={translateKey('common.notice')}
                        />
                    </PageTemplate>
                )}
            </FrontendSettingsContext.Consumer>
        );
    }

    private showEpisodeCategory() {
        return (
            <FormGroup
                hasErrors={this.state.errors.has('episodeCategoryId')}
                label={translateKey('episodeCategory.episodeCategory')}
            >
                <Dropdown
                    error={this.state.errors.get('episodeCategoryId')}
                    onChange={async (value) => {
                        await this.setState({
                            searchParams: {
                                ...this.state.searchParams,
                                episodeCategoryId: value,
                            },
                        });
                        await this.loadServices();
                    }}
                    placeholder={translateKey('episodeCategory.episodeCategory')}
                    selectOptions={this.state.searchParams.episodeCategoryFilterValues}
                    value={this.state.searchParams.episodeCategoryId}
                />
            </FormGroup>
        );
    }

    private isPatientInvoice(value: FilterChoice | null) {
        return value && [FilterChoice.compositePatientInvoice, FilterChoice.singularPatientInvoice].includes(value);
    }

    private async handleChange<K extends keyof ISearchParams>(name: K, value: ISearchParams[K]) {
        await this.setState(({ searchParams }) => ({ searchParams: { ...searchParams, [name]: value } }));
    }

    private async validateOne(name: keyof ISearchParams) {
        await this.setState(({ searchParams, errors }) => ({
            errors: updateValidationErrorsForOne(invoiceRecordsExportSchema, searchParams, errors, name),
        }));
    }

    private async loadServices() {
        let url = '/api/services?selectionList=true';
        if (this.state.searchParams.episodeCategoryId) {
            url += `&episodeCategoryId=${this.state.searchParams.episodeCategoryId}`;
        }
        const result = await fetchJson(url, 'GET');
        if (result.json.items) {
            const services: IService[] = result.json.items;

            const options = services.map((service) => {
                return {
                    group: translateKey(`episodeCategoryEnum.${service.episodeCategory.name}`),
                    text: service.name,
                    value: service.id,
                };
            });

            await this.setState({ serviceOptions: options });
        }
    }
    private async loadGroups(input?: string) {
        let options = [];
        if (input && input.length >= 2) {
            const url = `/api/groups/?searchText=${input}`;
            const result = await fetchJson(url, 'GET');
            if (result.json.items) {
                options = result.json.items.map((item: IGroup) => {
                    return { label: item.name, value: item.id };
                });
            }
        }

        return options;
    }

    private async loadEpisodeCategoryFilter(filterChoice: FilterChoice) {
        let url = '';
        if (filterChoice === FilterChoice.compositePatientInvoice) {
            url = `/api/episodeCategories?onlyCompositeFunding=true&debtorType=${DebtorType.patient}`;
        } else if (!this.isPatientInvoice(filterChoice)) {
            url = '/api/invoiceRecords/episodeCategoryFilters';
        } else {
            return;
        }

        const result = await fetchJson(url, 'GET');
        const episodeCategoryFilterValues = result.json.items
            .map((item: { id: string; name: string }) => {
                return {
                    text: translateKey(`episodeCategoryEnum.${item.name}`),
                    value: item.id,
                };
            })
            .sort((a: IFilterValues, b: IFilterValues) => {
                if (a.text > b.text) {
                    return 1;
                } else if (a.text < b.text) {
                    return -1;
                } else {
                    return 0;
                }
            });

        await this.setState({ searchParams: { ...this.state.searchParams, episodeCategoryFilterValues } });
    }

    private getUrl() {
        let url = this.getBaseUrlByFilterChoice() + '/export';

        if (this.state.searchParams.batchId) {
            url += '?batchId=' + this.state.searchParams.batchId;
        } else {
            url = this.addFiltersToUrl(url);
        }

        return url;
    }

    private getBatchType() {
        switch (this.state.searchParams.filterChoice) {
            case FilterChoice.compositePatientInvoice:
                return BatchType.patientInvoice;
            case FilterChoice.singularPatientInvoice:
                return BatchType.singularPatientInvoice;
            default:
                return BatchType.invoice;
        }
    }

    private getBaseUrlByFilterChoice() {
        switch (this.state.searchParams.filterChoice) {
            case FilterChoice.compositePatientInvoice:
                return '/api/finance/compositePatientInvoices';
            case FilterChoice.singularPatientInvoice:
                return '/api/singularPatientInvoiceRecords';
            default:
                return '/api/invoiceRecords';
        }
    }

    private async downloadExport() {
        const { errors } = validate(invoiceRecordsExportSchema, this.state.searchParams);
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }

        if (this.state.searchParams.batchId) {
            if (this.isPatientInvoice(this.state.searchParams.filterChoice)) {
                window.open(this.getUrl());
            } else {
                const result = await fetchJson(this.getUrl(), 'GET');
                await this.setState({
                    exportDownloadId: result.json.exportDownloadId,
                });
            }
        } else {
            const result = await fetchJson(this.getCountUrl(), 'GET');
            await this.setState({ count: result.json.count, showConfirmationModal: true });
        }
    }

    private async loadOrganizations(input?: string) {
        const options: IOption<string>[] = [];
        if (input && input.length >= 2) {
            const url = `/api/invoiceRecords/organizationsFromNotExportedInvoices?searchText=${input}`;
            const result = await fetchJson(url, 'GET');
            if (result.json.items) {
                result.json.items.map((organization: IOrganizationOptions) => {
                    const label = organization.name + (organization.city ? `, ${organization.city}` : '');
                    options.push({ label, value: organization.name });
                });
            }
        }

        return options;
    }

    private getConfirmationModalMessage() {
        if (!this.state.searchParams.closedBeforeDate) {
            return '';
        }

        let invoiceName = 'facturatieregels';
        if (this.state.searchParams.filterChoice === FilterChoice.compositePatientInvoice) {
            invoiceName = 'samengestelde patiëntfacturatieregels';
        }
        if (this.state.searchParams.filterChoice === FilterChoice.singularPatientInvoice) {
            invoiceName = 'enkelvoudige patiëntfacturatieregels';
        }

        let message = `Weet je zeker dat je ${this.state.count} ${invoiceName} tot en met de ${DateTime.from(
            this.state.searchParams.closedBeforeDate
        ).format('DD-MM-YYYY')}`;

        if (this.state.searchParams.serviceId) {
            const serviceName = this.state.serviceOptions.find(
                (service) => service.value === this.state.searchParams.serviceId
            )?.text;
            message += `, voor ${serviceName}`;
        }

        if (
            this.state.searchParams.filterChoice === FilterChoice.group &&
            this.state.searchParams.groupChoice === Choice.specific
        ) {
            message += ` en ${this.state.groupName}`;
        } else if (
            this.state.searchParams.filterChoice === FilterChoice.organization &&
            this.state.searchParams.organizationChoice === Choice.specific
        ) {
            message += ` en ${this.state.organizationName}`;
        }

        message += this.state.searchParams.isPreview ? ', wilt proefexporteren?' : ', wilt exporteren?';

        return message;
    }

    private getCountUrl() {
        const url = this.getBaseUrlByFilterChoice();

        return this.addFiltersToUrl(`${url}/exportCount`);
    }

    private addFiltersToUrl(url: string) {
        url += `?closedBeforeDate=${this.state.searchParams.closedBeforeDate}&isPreview=${this.state.searchParams.isPreview}`;

        if (this.state.searchParams.episodeCategoryId) {
            url += '&episodeCategoryId=' + this.state.searchParams.episodeCategoryId;
        }

        if (
            this.state.searchParams.serviceChoice === Choice.specific &&
            !this.isPatientInvoice(this.state.searchParams.filterChoice)
        ) {
            url += '&serviceId=' + this.state.searchParams.serviceId;
        }

        switch (this.state.searchParams.filterChoice) {
            case FilterChoice.group:
                if (this.state.searchParams.groupChoice === Choice.specific && this.state.searchParams.groupId) {
                    url += `&groupId=${this.state.searchParams.groupId}`;
                } else {
                    url += `&invoiceRecipient=${InvoiceRecipient.group}`;
                }
                break;
            case FilterChoice.organization:
                if (
                    this.state.searchParams.organizationChoice === Choice.specific &&
                    this.state.searchParams.organizationName
                ) {
                    url += `&organizationName=${this.state.searchParams.organizationName}`;
                } else {
                    url += `&invoiceRecipient=${InvoiceRecipient.organization}`;
                }
                break;
            default:
                break;
        }

        return url;
    }
}
